import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import TaskModal from '../tasks/TaskModal';
import ProjectModal from './ProjectModal';

const { FiArrowLeft, FiEdit3, FiArchive, FiCalendar, FiDollarSign, FiClock, FiCheckSquare, FiUser, FiInfo, FiTrendingUp, FiLayers, FiActivity, FiPlus, FiTrash2, FiCheck } = FiIcons;

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { projects, tasks, updateProject, deleteTask } = useData();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);

  const project = projects.find(p => p.id === id);
  const projectTasks = tasks.filter(t => t.project_name === project?.name || t.project === project?.name);

  if (!project) {
    return (
      <div className="h-full flex flex-col items-center justify-center space-y-4">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center text-slate-300">
          <SafeIcon icon={FiIcons.FiBriefcase} className="text-3xl" />
        </div>
        <p className="text-slate-400 font-bold text-xl">Project not found.</p>
        <button onClick={() => navigate('/projects')} className="text-blue-600 font-black uppercase text-xs tracking-widest hover:underline">
          Back to Projects
        </button>
      </div>
    );
  }

  const handleArchive = async () => {
    const action = project.isArchived ? 'Unarchive' : 'Archive';
    if (window.confirm(`${action} "${project.name}"?`)) {
      await updateProject(project.id, { isArchived: !project.isArchived });
      if (!project.isArchived) navigate('/projects');
    }
  };

  const calculateProgress = () => {
    if (projectTasks.length === 0) return 0;
    const completed = projectTasks.filter(t => t.status === 'Completed').length;
    return Math.round((completed / projectTasks.length) * 100);
  };

  const totalTimeSpentHours = projectTasks.reduce((sum, t) => sum + (t.time_spent || t.timeSpent || 0), 0) / 3600;

  return (
    <div className="space-y-8 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          <motion.button whileHover={{ x: -4 }} onClick={() => navigate('/projects')} className="p-4 bg-white rounded-2xl shadow-sm border border-slate-100 text-slate-400 hover:text-blue-600 transition-all">
            <SafeIcon icon={FiArrowLeft} />
          </motion.button>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-4xl font-black text-slate-900 tracking-tighter">{project.name}</h1>
              {project.isArchived && (
                <span className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">Archived</span>
              )}
            </div>
            <p className="text-slate-400 font-bold flex items-center gap-2 text-sm">
              <SafeIcon icon={FiUser} className="text-blue-500" /> {project.client || 'Internal Project'}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setIsEditModalOpen(true)} className="px-6 py-4 bg-white border border-slate-100 rounded-2xl font-black text-slate-600 hover:bg-slate-50 transition-all flex items-center gap-2 shadow-sm">
            <SafeIcon icon={FiEdit3} /> Edit Project
          </button>
          <button onClick={handleArchive} className={`px-6 py-4 rounded-2xl font-black transition-all flex items-center gap-2 shadow-sm ${project.isArchived ? 'bg-blue-600 text-white' : 'bg-slate-900 text-white'}`}>
            <SafeIcon icon={project.isArchived ? FiLayers : FiArchive} /> {project.isArchived ? 'Restore' : 'Archive'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white p-10 rounded-[3rem] shadow-sm border border-slate-100">
            <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                <SafeIcon icon={FiInfo} />
              </div> Project Overview
            </h3>
            <p className="text-slate-500 leading-relaxed font-medium text-lg mb-10">
              {project.description || 'No detailed description provided.'}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-10 border-t border-slate-50">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Start Date</p>
                <div className="flex items-center gap-2 font-bold text-slate-900">
                  <SafeIcon icon={FiCalendar} className="text-blue-500 text-xs" /> {project.startDate ? new Date(project.startDate).toLocaleDateString() : 'Not set'}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Deadline</p>
                <div className="flex items-center gap-2 font-bold text-red-500">
                  <SafeIcon icon={FiClock} className="text-xs" /> {project.deadline ? new Date(project.deadline).toLocaleDateString() : 'No deadline'}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-10 rounded-[3rem] shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-10">
              <h3 className="text-xl font-black text-slate-900 flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <SafeIcon icon={FiCheckSquare} />
                </div> Tasks ({projectTasks.length})
              </h3>
              <button onClick={() => setIsTaskModalOpen(true)} className="bg-blue-600 text-white px-6 py-3 rounded-2xl font-black shadow-lg shadow-blue-100 flex items-center gap-2 hover:bg-blue-700 transition-all text-sm">
                <SafeIcon icon={FiPlus} /> New Task
              </button>
            </div>
            <div className="space-y-4">
              {projectTasks.map(task => (
                <div key={task.id} className="flex items-center justify-between p-5 bg-slate-50/50 rounded-[2rem] hover:bg-slate-50 transition-all group border border-transparent hover:border-slate-100">
                  <div className="flex items-center gap-5">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg shadow-sm ${task.status === 'Completed' ? 'bg-green-500 text-white' : 'bg-white text-slate-400'}`}>
                      {task.status === 'Completed' ? <SafeIcon icon={FiCheck} /> : <SafeIcon icon={FiClock} />}
                    </div>
                    <div>
                      <p className="font-black text-slate-900 text-lg">{task.title}</p>
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{task.assignee || 'Unassigned'}</span>
                    </div>
                  </div>
                  <button onClick={() => deleteTask(task.id)} className="p-3 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="bg-slate-900 p-10 rounded-[3.5rem] text-white shadow-2xl relative overflow-hidden">
            <SafeIcon icon={FiTrendingUp} className="absolute -right-6 -bottom-6 text-white/5 text-[12rem] pointer-events-none" />
            <h3 className="text-xl font-black mb-8 flex items-center gap-3">
              <SafeIcon icon={FiActivity} className="text-blue-400" /> Progress
            </h3>
            <div className="space-y-8 relative z-10">
              <p className="text-6xl font-black">{calculateProgress()}%</p>
              <div className="h-4 bg-white/10 rounded-full overflow-hidden p-1">
                <motion.div initial={{ width: 0 }} animate={{ width: `${calculateProgress()}%` }} transition={{ duration: 1 }} className="h-full bg-blue-600 rounded-full" />
              </div>
            </div>
          </div>

          <div className="bg-white p-10 rounded-[3.5rem] shadow-sm border border-slate-100">
            <h3 className="text-xl font-black text-slate-900 mb-8 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-green-600">
                <SafeIcon icon={FiDollarSign} />
              </div> Financials
            </h3>
            <div className="space-y-6">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400 font-black uppercase tracking-widest text-[10px]">Budget</span>
                <span className="font-black text-slate-900 text-xl">${project.budget?.toLocaleString() || 0}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400 font-black uppercase tracking-widest text-[10px]">Time Tracked</span>
                <span className="font-black text-slate-600">{totalTimeSpentHours.toFixed(2)}h</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProjectModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} project={project} />
      <TaskModal isOpen={isTaskModalOpen} onClose={() => setIsTaskModalOpen(false)} initialProject={project.name} />
    </div>
  );
};

export default ProjectDetail;